/**
 * router/parse.ts —— hash 解析
 * 来历：一期 [SEC:router] 段的 parseHash()，逻辑【一字未改】，只加类型 + 拆成纯函数。
 *
 * 保持纯函数的理由：它是全站唯一把 URL 翻成意图的地方，可以单测（parse.test.ts），
 * 而单测是二期唯一的回归手段——26 天里没有集中测试期（§三之三 约定 3）。
 *
 * ⚠ 【不要在这里读数据、不要在这里改写 hash】。
 *   「这支队存不存在」「这个期号合不合法」属于守卫（guards.ts），不属于解析。
 *   两件事混在一起，就再也写不出不依赖网络的单测了。
 */

export type Route =
  | { view: 'login' }
  | { view: 'overview' }
  | { view: 'teams' }
  | { view: 'detail'; team: string; from: 'teams' | 'overview' }
  | { view: 'okr'; team: string | null }
  | { view: 'reports'; no: number | null; team: string | null; edit: boolean }
  | { view: 'checkins'; team: string | null }
  | { view: 'bulletins'; no: number | null; id: string | null }
  | { view: 'admin'; tab: string | null };
/* ⚠ 【没有 assistant 路由】（2026-08-24 起）。AI 助手改成了运营总览 Hero 上的
   **右侧抽屉**（`shared/assistant.ts`），不是一个页面。旧链接 `#/assistant`
   会落到下面 parseHash 的 `return null`，由 route() 回落首页——这是正确行为，不要"补回来"。
   决策留痕见 `03_归档/00_决策留痕.md` §二 26。 */

/** 未知路由返回 null，由 route() 回落到首页——不报错、不白屏（一期原则） */
export function parseHash(hash: string): Route | null {
  const seg = (hash || '').replace(/^#\/?/, '').split('/').filter(Boolean);
  if (!seg.length) return { view: 'overview' };
  if (seg[0] === 'login') return { view: 'login' };
  if (seg[0] === 'teams') return seg[1] ? { view: 'detail', team: seg[1], from: 'teams' } : { view: 'teams' };
  if (seg[0] === 'overview') return seg[1] ? { view: 'detail', team: seg[1], from: 'overview' } : { view: 'overview' };
  if (seg[0] === 'okr') return { view: 'okr', team: seg[1] || null };
  /* 双周报三级路由：#/reports/p3 列表 → /T01 详情 → /edit 编辑。
     期号带 p 前缀，与队伍号 T01 一眼可分，一条正则就能分流；
     裸 #/reports 与旧链接 #/reports/T01 都合法（期号缺省），交给守卫改写。 */
  if (seg[0] === 'reports') {
    if (seg[1] && /^p\d+$/.test(seg[1]))
      return { view: 'reports', no: +seg[1].slice(1), team: seg[2] || null, edit: seg[3] === 'edit' };
    return { view: 'reports', no: null, team: seg[1] || null, edit: seg[2] === 'edit' };
  }
  if (seg[0] === 'checkins') return { view: 'checkins', team: seg[1] || null };
  /* 项目快报：`#/bulletins/p3`。
     🔴 **必须带周期**（2026-08-25，`16` §四之二）——否则刷新丢选择，也没法把
       「上一期那份快报」的链接直接发给人。
     ⚠ 旧链接 `#/bulletins/all` 是**单队 / 整体两种范围**时代的产物，范围那一维已经砍掉了；
       它落到下面的 `no: null`，由守卫改写成默认期，不报错、不白屏。 */
  /* ⚠ 第三段可选：`#/bulletins/p3/B…` = 查看这一期的某一份历史版本（2026-09-28）。
     同样为了刷新不丢、链接能直接发给人。只认 B 开头的 id，其余当没写。 */
  if (seg[0] === 'bulletins') {
    const no = seg[1] && /^p\d+$/.test(seg[1]) ? +seg[1].slice(1) : null;
    const id = no != null && seg[2] && /^B[A-Za-z0-9]+$/.test(seg[2]) ? seg[2] : null;
    return { view: 'bulletins', no, id };
  }
  if (seg[0] === 'admin') return { view: 'admin', tab: seg[1] || null };
  return null;
}
